import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Swords, AlertTriangle, CheckCircle2, XCircle, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TournamentMatchPage() {
  const { tournamentId, matchId } = useParams();
  const navigate = useNavigate();

  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Quiz state
  const [started, setStarted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [answers, setAnswers] = useState({});
  const [tabSwitches, setTabSwitches] = useState(0);
  const [finished, setFinished] = useState(false);
  const [result, setResult] = useState(null);

  const tabSwitchRef = useRef(0);

  useEffect(() => {
    axios.get(`${API}/tournaments/${tournamentId}/match/${matchId}`)
      .then(res => {
        setMatch(res.data);
        if (res.data.already_submitted) {
          setError("You've already submitted your answers for this match.");
        }
      })
      .catch(err => setError(err.response?.data?.detail || "Failed to load match"))
      .finally(() => setLoading(false));
  }, [tournamentId, matchId]);

  // Anti-cheat: detect tab switches
  useEffect(() => {
    if (!started || finished) return;
    const handleVisibility = () => {
      if (document.hidden) {
        tabSwitchRef.current += 1;
        setTabSwitches(tabSwitchRef.current);
        // Auto-skip current question (no answer = wrong)
        handleNext(true);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [started, finished, current]);

  const questions = match?.questions || [];
  const total = questions.length;
  const q = questions[current];

  const handleAnswer = (option) => {
    if (showFeedback || finished) return;
    setSelected(option);
    setAnswers(prev => ({ ...prev, [current]: option }));
    setShowFeedback(true);
  };

  const handleNext = useCallback((skip = false) => {
    if (!skip) setShowFeedback(false);
    setSelected(null);
    if (current + 1 >= total) {
      submitMatch();
    } else {
      setCurrent(c => c + 1);
      setShowFeedback(false);
    }
  }, [current, total]);

  const submitMatch = async () => {
    setFinished(true);
    try {
      const res = await axios.post(`${API}/tournaments/${tournamentId}/match/${matchId}/submit`, {
        answers,
        time_taken: {},
        tab_switches: tabSwitchRef.current,
      });
      setResult(res.data);
    } catch (err) {
      if (err.response?.status !== 400) {
        setError(err.response?.data?.detail || "Failed to submit");
      } else {
        setResult({ score: Object.values(answers).length, total });
      }
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
      <p className="text-[hsl(var(--muted-foreground))]">Loading match...</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col items-center justify-center gap-4 p-4">
      <AlertTriangle size={36} className="text-[#C36A2F]" />
      <p className="text-[hsl(var(--muted-foreground))] text-center">{error}</p>
      <Button variant="outline" onClick={() => navigate("/tournaments")} data-testid="match-back-error">Back to Tournaments</Button>
    </div>
  );

  // Results screen
  if (finished) return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
      <div className="max-w-md w-full rounded-xl border bg-[hsl(var(--card))] border-[hsl(var(--border))] overflow-hidden">
        <div className="p-4 text-center bg-[hsl(var(--secondary))]">
          <Swords size={32} className="mx-auto text-[hsl(var(--primary))] mb-2" />
          <p className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Match Complete</p>
        </div>
        <div className="p-6 text-center space-y-4">
          {result && (
            <>
              <p className="text-4xl font-bold text-[hsl(var(--primary))]" data-testid="match-score">{result.score}/{result.total}</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Waiting for your opponent to finish...
              </p>
            </>
          )}
          {tabSwitches > 0 && (
            <p className="text-xs text-red-400 flex items-center justify-center gap-1">
              <AlertTriangle size={12} /> {tabSwitches} tab switch(es) detected — those questions were skipped
            </p>
          )}
          <Button onClick={() => navigate("/tournaments")} className="w-full bg-[hsl(var(--primary))] text-black" data-testid="match-back-done">
            Back to Tournaments
          </Button>
        </div>
      </div>
    </div>
  );

  // Start screen
  if (!started) return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
      <div className="max-w-md w-full rounded-xl border bg-[hsl(var(--card))] border-[hsl(var(--border))] overflow-hidden">
        <div className="p-4 text-center bg-[hsl(var(--secondary))]">
          <Swords size={40} className="mx-auto text-[hsl(var(--primary))] mb-2" />
          <p className="text-lg font-bold">Tournament Match</p>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--secondary))]">
            <span className="font-bold text-[hsl(var(--primary))]">{match.player1_name}</span>
            <span className="text-xs text-[hsl(var(--muted-foreground))]">VS</span>
            <span className="font-bold text-[hsl(var(--primary))]">{match.player2_name}</span>
          </div>
          <div className="space-y-2 text-sm text-[hsl(var(--muted-foreground))] text-center">
            <p className="flex items-center justify-center gap-2"><Zap size={14} /> {total} questions</p>
            {match.is_sudden_death && (
              <p className="text-red-400 font-bold flex items-center justify-center gap-2">
                <AlertTriangle size={14} /> SUDDEN DEATH — Tiebreaker round!
              </p>
            )}
          </div>
          <div className="rounded-lg border border-[#C36A2F]/30 bg-[#C36A2F]/5 p-3 text-xs text-[#C36A2F]">
            <p className="font-bold mb-1">Fair Play Rules:</p>
            <ul className="space-y-0.5 list-disc pl-4">
              <li>Switching tabs will auto-skip the question</li>
              <li>Questions are randomised for each player</li>
            </ul>
          </div>
          <Button onClick={() => setStarted(true)} className="w-full bg-[hsl(var(--primary))] text-black font-bold" data-testid="start-match-btn">
            Start Match
          </Button>
        </div>
      </div>
    </div>
  );

  // Quiz screen (no timer)
  const progress = ((current + 1) / total) * 100;
  const isCorrect = selected && q && selected === q.correct_answer;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] p-4">
      <div className="max-w-lg mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-xs text-[hsl(var(--muted-foreground))]">{current + 1}/{total}</span>
          <div className="flex items-center gap-2">
            <Swords size={14} className="text-[hsl(var(--primary))]" />
            <span className="text-xs font-bold text-[hsl(var(--primary))]">Tournament</span>
          </div>
          <span />
        </div>

        <Progress value={progress} className="h-1.5 bg-[hsl(var(--secondary))]" />

        {q && (
          <div className="rounded-xl border border-[hsl(var(--border))] overflow-hidden bg-[hsl(var(--card))]" data-testid={`match-question-${current}`}>
            <div className="px-4 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--primary))]">{q.type}</p>
              <p className="text-xs mt-0.5 text-[hsl(var(--muted-foreground))]">{q.cocktail_name}</p>
            </div>
            <div className="p-4">
              <p className="text-sm font-medium mb-4">{q.question_text}</p>
              <div className="space-y-2">
                {q.options.map((opt, i) => {
                  let cls = "border-[hsl(var(--border))] bg-[hsl(var(--secondary))] hover:border-[hsl(var(--primary))]/50";
                  if (showFeedback && selected === opt) {
                    cls = selected === opt ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "";
                  }
                  return (
                    <button key={`${q.id || 'q'}-${opt}`} onClick={() => handleAnswer(opt)} disabled={showFeedback}
                      className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-all ${cls}`}
                      data-testid={`match-option-${i}`}
                    >{opt}</button>
                  );
                })}
              </div>
            </div>
            {showFeedback && (
              <div className="px-4 pb-4">
                <Button onClick={() => handleNext()} className="w-full font-bold bg-[hsl(var(--primary))] text-black" data-testid="match-next">
                  {current + 1 >= total ? "Submit Match" : "Next Question"}
                </Button>
              </div>
            )}
          </div>
        )}

        {tabSwitches > 0 && (
          <p className="text-xs text-red-400 text-center flex items-center justify-center gap-1">
            <AlertTriangle size={12} /> Tab switch detected — question was skipped
          </p>
        )}
      </div>
    </div>
  );
}
